import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import { ProgramService } from '../../../../services/vpp/program.service';
import {ActivatedRoute} from '@angular/router';
import {forkJoin, Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MapLocations, Program} from '../../../../domain/cloud-topology';
import {DataService} from '../../../../services/data.service';
import {SiteService} from '../../../../services/site/site.service';
import {ProgramEditModalComponent} from '../../program-edit-modal/program-edit-modal.component';
import {SideNavService} from "../../../../services/side-nav.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {cancelStatus, saveStatus} from "../../../../domain/constants";

@Component({
    selector: 'app-program-edit',
    templateUrl: './program-edit.component.html',
    standalone: false,
  styleUrls: ['./program-edit.component.scss'],
})
export class ProgramEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() program: Program;
  private subject$: Subject<void>;
  maxOutput: number;
  maxEnergy: number;
  programForm: [];
  programSites: any[];
  programSitesSubscriptions: Observable<unknown>[];
  mapLocations: MapLocations[];

  constructor(private programService: ProgramService,
              private siteService: SiteService,
              private snackBar: MatSnackBar,
              private dialogService: MatDialog,
              private sideNavService: SideNavService) {
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['program']['currentValue'] !== undefined) {
      this.program = changes['program']['currentValue'];
      this.getSiteData();
    }
  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  getSiteData(): void {
    this.subject$ = new Subject<void>();
    this.programSites = [];
    this.mapLocations = [];
    this.programSitesSubscriptions = [];

    this.programService.getProgramSites(this.program).pipe(takeUntil(this.subject$)).subscribe(sites => {
      this.sideNavService.getSiteCache().pipe(takeUntil(this.subject$)).subscribe(siteCache => {
        if (siteCache) {
          for (const site of sites) {
            if (siteCache.find((siteToFind) => {
              return site.id == siteToFind.id;
            })) {
              this.programSites.push(site.id);
              this.programSitesSubscriptions.push(this.siteService.getSite(site.id, true));
            }
          }

          forkJoin(this.programSitesSubscriptions).pipe(takeUntil(this.subject$)).subscribe(data => {
            for (const site of data) {
              this.mapLocations.push({
                lat: site['latitude'],
                long: site['longitude'],
                description:  `${site['name']} <br /> ${site['timezone']}`
              });
            }
          });
        }
      });
    });
  }

  processNewSites($event) { // Executed after app-program-sites emits new selections / deletions.
    const sites = $event;
    const sitesToUpdate = [];
    for (const site of sites) {
      sitesToUpdate.push(site.id);
    }
    this.program.sites = sitesToUpdate;
    this.getSiteData();
  }

  canDeactivate() {
    if (this.program.name !== this.programForm['name']) {
      return window.confirm('Are you sure you want to discard changes?');
    }

    return true;
  }

  openModal() {
    const dialogRef = this.dialogService.open(ProgramEditModalComponent, {
      width: '1000px',
      data: {
        program: this.program
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.status == saveStatus) {
        const programFormValues = result['program'];
        this.programForm = programFormValues;
        this.program.name = programFormValues['name'];
        this.program.programType = programFormValues['programType'];
        this.program.properties.nominatedKw = programFormValues['nominatedKw'];
        this.program.properties.pointOfMeasurement = programFormValues['pointOfMeasurement'];
        this.program.properties.performanceTarget = programFormValues['performanceTarget'];
        this.program.properties.notificationTimeframe = programFormValues['notificationTimeframe'];
        this.program.properties.marketAuction = (programFormValues['marketAuction'] == 'true');
        this.program.properties.forwardStats = (programFormValues['forwardStats']);
        this.program.properties.programIncentive = programFormValues['incentive'];
        this.program.properties.programPenalty = programFormValues['penalty'];
        this.program.properties.forceMaxDispatch = programFormValues['forceMaxDispatch'];
        this.programService.putProgram(this.program.id, this.program).pipe(takeUntil(this.subject$)).subscribe(() => {
          this.snackBar.open(`${this.program.name} has successfully been updated`, "Close", {
            duration: 3000,
            horizontalPosition: "right",
            verticalPosition: "bottom"
          });
        }, () => {
          this.snackBar.open(`Error updating program.`, "Close",{
            duration: 3000,
            horizontalPosition: "right",
            verticalPosition: "bottom"
          });
        });
      }

      if (result?.status == cancelStatus) {
        this.snackBar.open("Changes have been discarded", "Close", {
          duration: 3000,
          horizontalPosition: "right",
          verticalPosition: "bottom"
        });
      }
    });
  }
}
